import { TutorialConfig, TutorialStep, TutorialTask } from '../types';

const SHOTCUT_TUTORIAL_PROJECT_ID = 'feb13608-ba74-4146-b2f1-06db304a238b';

const SHOTCUT_TUTORIAL_TASKS: TutorialTask[] = [
  {
    id: 'shotcut-project-tour',
    name: 'Explore the labeling workspace',
    type: 'success',
  },
  {
    id: 'shotcut-obvious-failure',
    name: 'Obvious failure',
    type: 'failure',
    runId: 'df8b135a-2d25-491f-818b-6209eb99eda2',
  },
  {
    id: 'shotcut-obvious-success',
    name: 'Obvious success',
    type: 'success',
    runId: 'f665414d-e76d-47d7-81a2-eade22fc1626',
  },
  {
    id: 'shotcut-ambiguous-bad-task',
    name: 'Ambiguous task',
    type: 'bad_task',
    runId: 'f9318240-ed5d-4182-b0c0-e501c22a3ff3',
  },
  {
    id: 'shotcut-ffmpeg-pass',
    name: 'FFmpeg success',
    type: 'success',
    runId: '7f4aeb68-2023-4a6e-89fd-2a9da38c9bec',
  },
  {
    id: 'shotcut-subtle-failure',
    name: 'Subtle timing failure',
    type: 'failure',
    runId: 'dc836189-0dd2-43d9-bdf2-6c81663a1173',
  },
];

const SHOTCUT_TUTORIAL_STEPS: TutorialStep[] = [
  // ============================================================================
  // SHARED PROJECT TOUR FRAMES (frames 1-13)
  // Keep these identical across all tutorials
  // ============================================================================
  {
    id: 'shotcut-project-welcome',
    title: 'Welcome to Shotcut Labeling',
    content:
      'This tutorial gives you a quick tour of the labeling workspace. You can explore every part of the UI without affecting real data.\n\nOnce you claim a labeling project, you can click on the project to be brought to this page.\n\nIf you would like to exit the tutorial, just click "Exit Tutorial" on the top bar. You can always restart the tutorial by clicking "Show Tutorial."',
    targetSelector: '.problem-detail',
    action: 'next',
    position: 'bottom',
    scope: 'project',
    nextButtonLabel: 'Next Problem',
  },
  {
    id: 'shotcut-problem-list',
    title: 'Assigned Problems',
    content:
      'Each card in this list represents a problem. The badge tracks how many runs you have labeled. Feel free to click through the list during the tutorial.',
    targetSelector: '.problem-list',
    action: 'explore',
    position: 'right',
    allowInteraction: true,
    scope: 'project',
  },
  {
    id: 'shotcut-solution-upload',
    title: 'Solution Uploads',
    content:
      'Projects consist of two steps: first, you must upload the solution for the problem, and next, you should label all of the runs for that problem. When uploading your solution, make sure that your uploaded filename matches exactly the filename of the solution requested in the prompt.',
    targetSelector: '.solution-upload-section',
    action: 'next',
    position: 'left',
    scope: 'project',
    maxWidth: 320,
  },
  {
    id: 'shotcut-bad-task-button',
    title: 'Mark Ambiguous Problems',
    content:
      'If the prompt is ambiguous or has multiple valid outcomes, mark it as a bad task here. Provide a concise reason so reviewers understand the issue. After marking as ambiguous, all runs will automatically be labeled and you will not have to upload a solution.',
    targetSelector: '.bad-task-btn',
    action: 'next',
    position: 'bottom',
    scope: 'project',
  },
  {
    id: 'solution-upload-requirement',
    title: 'Solution Required',
    content:
      'Important: You must upload your solution before you can select and label any runs. This ensures you have solved the problem yourself before evaluating the AI\'s attempts. Notice that the runs below are currently grayed out.',
    targetSelector: '.run-list',
    action: 'next',
    position: 'left',
    scope: 'project',
    maxWidth: 320,
  },
  {
    id: 'solution-upload-select-run',
    title: 'Runs to Review',
    content:
      'Every run shows a model attempt. Now you can select one to open the viewer so you can study exactly how the agent approached the task. Click on any run below to proceed.',
    targetSelector: '.run-list',
    action: 'click',
    position: 'left',
    allowInteraction: true,
    scope: 'project',
    nextButtonLabel: 'Select a run to proceed',
  },
  {
    id: 'shotcut-viewer-overview',
    title: 'Review Each Attempt',
    content:
      'The video displays the AI solving the task. The AI has a computer environment consisting of many applications like Audacity, LibreOffice, Inkscape, Gimp, and Shotcut, and can use any of these applications to solve the task. The AI can also use programming and bash scripting. Anything is fair game as long as the AI solves the task as requested!',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'shotcut-viewer-timeline',
    title: 'Timeline Controls',
    content:
      'Use the timeline to jump between frames. Speed controls and keyboard shortcuts help you inspect long attempts quickly without skipping critical moments.',
    targetSelector: '.timeline-container',
    action: 'next',
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'shotcut-viewer-command-panel',
    title: 'Command History',
    content:
      'The agent can launch shell commands, scripts, or custom tools while it works. Every command it runs appears here so you can verify exactly what code or terminal actions were executed.',
    targetSelector: '.tool-output-section:first-child',
    action: 'next',
    position: 'top',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'shotcut-viewer-thoughts',
    title: 'Agent Notes',
    content:
      'Read the thought bubble to see why the agent acted. When judging ambiguity, these notes often reveal whether the prompt had multiple valid interpretations.',
    targetSelector: '.thought-process',
    action: 'next',
    position: 'right',
    scope: 'viewer',
  },
  {
    id: 'shotcut-viewer-inspect',
    title: 'Inspect the Attempt',
    content:
      'Watch the video carefully to see every step the AI took. When you need the final output, right-click the video to download the files it produced and compare them with your ground-truth solution.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'shotcut-viewer-evaluate',
    title: 'Label the Run',
    content:
      'Use this panel to decide whether the run succeeded or failed. If you realize that a problem is ambiguous or has multiple correct solutions, return to the problem workspace, mark it as a bad task, and then move on to the next problem.',
    targetSelector: '.evaluation-buttons',
    action: 'next',
    position: 'top',
    maxWidth: 360,
    scope: 'viewer',
    offsetY: -50,
  },
  {
    id: 'shotcut-viewer-return',
    title: 'Return to the Problem Workspace',
    content:
      'Click "Back to Problem" when you finish reviewing runs. That returns you to the overview so you can upload a solution or mark the problem ambiguous.',
    targetSelector: '.back-to-problem-btn',
    action: 'click',
    position: 'right',
    scope: 'viewer',
    allowInteraction: true,
  },
  {
    id: 'shotcut-project-recap',
    title: 'Wrap Up the Problem',
    content:
      'From this workspace you can revisit the prompt, upload the required solution file, and finish labeling every run. Ambiguous problems can be waived entirely.',
    targetSelector: '.problem-detail',
    action: 'next',
    position: 'bottom',
    scope: 'project',
  },
  {
    id: 'shotcut-example-transition',
    title: 'Run Labeling Examples',
    content:
      'Now let us review real AI runs and decide whether each is a success or a failure. We will walk through several examples together.',
    targetSelector: '.problem-detail',
    action: 'next',
    position: 'bottom',
    scope: 'project',
    nextButtonLabel: 'Next Problem',
  },

  // ============================================================================
  // SHOTCUT-SPECIFIC EXAMPLE STEPS
  // ============================================================================

  // Example 1: Obvious Failure
  {
    id: 'shotcut-obvious-failure-intro',
    title: 'Obvious Failure',
    content:
      'The AI obviously fails at completing this task. You can see that the AI uses Shotcut throughout without using the terminal. However, the AI completely fails at adding the parameters to the provided effects. Watch carefully and you will see the effect settings never change.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'shotcut-obvious-failure-conclusion',
    title: 'Mark as Failure',
    content:
      'Because the AI never successfully applies the required effect parameters, this run is an obvious fail. Mark it as a failure and move on.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'fail',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 2: Obvious Success
  {
    id: 'shotcut-obvious-success-intro',
    title: 'Obvious Success',
    content:
      'The AI solves the task perfectly in Shotcut. If you compare the AI\'s output with the solution and go frame-by-frame, you will see that they look identical! Tip: you can right click the video and use the side-by-side video viewer to quickly compare solutions.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
    offsetY: 600,
  },
  {
    id: 'shotcut-obvious-success-conclusion',
    title: 'Clear Success',
    content:
      'When the output matches your solution exactly, confidently mark the run as a success.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'success',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 3: Bad Task (Ambiguous)
  {
    id: 'shotcut-bad-task-intro',
    title: 'Ambiguous Task',
    content:
      'At first, this task looks totally unambiguous. As a Shotcut user, there is only one answer: there is only one way to assemble the clips, and contrast modifications should be applied to each clip.',
    targetSelector: '.task-description',
    action: 'next',
    position: 'bottom',
    scope: 'viewer',
    offsetY: 40,
  },
  {
    id: 'shotcut-bad-task-details',
    title: 'Software-Dependent Defaults',
    content:
      'However, remember that the AI can solve this problem however it wants. Note that in this case, the AI decides to adjust the contrast via FFmpeg rather than using Shotcut. The AI actually reduces the contrast by 60% rather than increasing it from 50% to 60%! This is because the default contrast value is only defined in Shotcut, and is not a fundamental property of images.',
    targetSelector: '.tool-output-wrapper',
    action: 'seek-frame',
    seekFrame: 39,
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'shotcut-bad-task-conclusion',
    title: 'Multiple Valid Interpretations',
    content:
      '"Apply 60% contrast" has different meanings across software, making this a bad task—solutions must be unambiguous. Always carefully analyze AI runs, as failures can stem from task ambiguity. When you find these examples, you should navigate back to the problem page and mark it as a bad task. This will quickly mark the entire problem and all of its labels as complete!',
    targetSelector: '.evaluation-buttons',
    action: 'next',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
  },

  // Example 4: FFmpeg Pass
  {
    id: 'shotcut-ffmpeg-intro',
    title: 'Looks Wrong, Actually Right',
    content:
      'At first, the AI tries to use Shotcut, and it struggles. Watch carefully throughout the run.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'shotcut-ffmpeg-code',
    title: 'FFmpeg to the Rescue',
    content:
      'Look carefully: the AI is also allowed to use FFmpeg. And indeed, after giving up on Shotcut, the AI manually programs a cross-fade transition! If you download the video, you will indeed see that the results are exactly the same.',
    targetSelector: '.tool-output-wrapper',
    action: 'seek-frame',
    seekFrame: 56,
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'shotcut-ffmpeg-conclusion',
    title: 'Always Download and Compare',
    content:
      'This is why, if you see that the AI is using FFmpeg, it is important to download the video each time and compare it yourself manually. Otherwise, you will mislabel these kinds of videos! This run is a success.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'success',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 5: Subtle Failure (Timing)
  {
    id: 'shotcut-timing-intro',
    title: 'Looks Right, But Check Carefully',
    content:
      'On this one, the AI gives up on using Shotcut and uses FFmpeg instead. When you compare the videos, at first, the video looks almost correct; the cut happens around 5s as requested.',
    targetSelector: '.tool-output-wrapper',
    action: 'next',
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'shotcut-timing-details',
    title: 'Precision Matters',
    content:
      'However, when you check the side-by-side video viewer, you will notice that the cut actually happens beyond the 5s mark, instead happening 50ms beyond at 5:05. Be careful with these!',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'shotcut-timing-conclusion',
    title: 'Frame-Level Accuracy Required',
    content:
      'It is fine if the AI\'s solution is within 10ms of the actual solution, but if it is at least one frame off, then you should mark it as a fail (and explain the minute difference in the failure reason). This is a failure.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'fail',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Final Step
  {
    id: 'tutorial-complete',
    title: 'Nice Work!',
    content:
      'You have finished the Shotcut tutorial. You now know how to tour the workspace, inspect outputs, download and compare videos, and decide between success, fail, or bad task. Remember: always download and compare outputs when FFmpeg is involved, and pay close attention to timing precision. Good luck on real projects!',
    action: 'next',
    position: 'bottom',
    scope: 'viewer',
  },
];

export const shotcutTutorialConfig: TutorialConfig = {
  software: 'shotcut',
  displayName: 'Shotcut Labeling Tutorial',
  totalTasks: SHOTCUT_TUTORIAL_TASKS.length,
  tasks: SHOTCUT_TUTORIAL_TASKS,
  steps: SHOTCUT_TUTORIAL_STEPS,
  completionKey: 'labelPlatform_shotcut_TutorialCompleted',
  dataSource: 'api',
  projectId: SHOTCUT_TUTORIAL_PROJECT_ID,
};
